import React from 'react';
import { DebounceInput } from 'react-debounce-input';

import { bemBlock, Icon, Layout } from '@neptune/shared/venus-ui';

import { SearchInputIcon } from './SearchInputIcon';
import { SearchInputProps } from './types';

import './SearchInput.less';

const block = bemBlock('app-search-input');

export const SearchInput: React.FC<SearchInputProps> = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  autoFocus,
  className,
  debounceTimeout = 200,
  disabled,
  elementRef,
  onApply,
  onBlur,
  onChange,
  onValueChange,
  onFocus,
  onKeyDown,
  onKeyUp,
  onClear,
  placeholder,
  value,
  size = 'medium',
  variant = 'basic',
  withClear = true,
  withIcon = false,
  glyph = 'search',
  'data-role': dataRole = 'search-input',
  HelpInfo,
  span = 'auto',
  withPadding = 'none',
  spacedChildren,
  inputRef,
  suffixElement,
  hasError = false,
  withBorder = true,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    if (disabled) {
      setIsFocused(false);
    }
  }, [disabled]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event.target.value);
    },
    [onChange],
  );

  const handleClear = React.useCallback(() => {
    onValueChange?.('');
    onChange?.('');
    onClear?.();
  }, [onChange, onClear, onValueChange]);

  const handleInput = React.useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      onValueChange?.(event.currentTarget.value);
    },
    [onValueChange],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onApply?.(event.currentTarget.value);
        event.preventDefault();
      }

      onKeyDown?.(event);
    },
    [onApply, onKeyDown],
  );

  const handleKeyUp = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        handleClear();
      }

      onKeyUp?.(event);
    },
    [handleClear, onKeyUp],
  );

  const handleFocus = React.useCallback(
    (event: React.FocusEvent) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = React.useCallback(
    (event: React.FocusEvent) => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  let searchIcon: React.ReactNode = null;

  if (React.isValidElement(withIcon)) {
    searchIcon = withIcon;
  } else if (withIcon) {
    searchIcon = <SearchInputIcon className={block('icon')} size={size} glyph={glyph} />;
  }

  const clearButton =
    value.length > 0 && withClear ? (
      <div onClick={handleClear} className={block('clear-button')} data-role="clear-search">
        <Icon glyph="cross" />
      </div>
    ) : null;

  const helpInfoElement =
    HelpInfo && value.length > 0 ? <HelpInfo className={block('help-button')} /> : null;

  return (
    <Layout.Row
      className={block({
        extra: className,
        modifiers: {
          size,
          variant,
          disabled: disabled === true,
          focused: isFocused,
          'invalid-value': hasError,
          'without-border': !withBorder,
        },
      })}
      alignItems="stretch"
      elementRef={elementRef}
      span={span}
      withPadding={withPadding}
      spacedChildren={spacedChildren}
    >
      {searchIcon}
      <DebounceInput
        debounceTimeout={debounceTimeout}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onChange={handleChange}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-role={dataRole}
        inputRef={inputRef}
        data-error={hasError}
      />
      <Layout.Row span="shrink" alignItems="center">
        {helpInfoElement}
        {clearButton}
        {suffixElement}
      </Layout.Row>
    </Layout.Row>
  );
};
